import { useId, useState } from 'react';
import { Link, useParams } from 'react-router';
import { ROUTES } from '@/app/router/routes';
import {
  BriefcaseIcon,
  BuildingContactsIcon,
  CalendarIcon,
  ChevronRightIcon,
  InvoiceIcon,
  LayoutGridIcon,
  TimesheetIcon,
} from '@/components/icons';
import { LoadingState, ErrorNotice } from '@/components/portal/PortalPage';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DetailSection, Field, FieldGrid } from '@/components/ui/DetailSection';
import { Tabs, TabPanel } from '@/components/ui/Tabs';
import { TimesheetDeclaration } from '@/features/timesheets/TimesheetDeclaration';
import { formatMissionDate } from '@/features/timesheets/mission-periods';
import { formatMonth, formatDays, STATE_TONES } from '@/features/timesheets/timesheet-labels';
import { useMyTimesheets } from '@/features/timesheets/useTimesheets';
import type { PortalMissionDetail } from './types';
import { useMission } from './usePortal';

type MissionTab = 'general' | 'financial' | 'timesheets';

const MissionDetails = ({ mission }: { mission: PortalMissionDetail }) => {
  const tabsId = useId();
  const [tab, setTab] = useState<MissionTab>('general');
  const sheets = useMyTimesheets({ missionId: mission.id });
  const cost =
    mission.costDailyRate === null
      ? 'Non renseigné'
      : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: mission.currency }).format(
          Number(mission.costDailyRate),
        );
  const tabs = [
    { value: 'general' as const, label: 'Généralités', icon: <LayoutGridIcon /> },
    { value: 'financial' as const, label: 'Financier', icon: <InvoiceIcon /> },
    {
      value: 'timesheets' as const,
      label: 'Comptes rendus d’activité',
      icon: <TimesheetIcon />,
      ...(sheets.data ? { count: sheets.data.meta.total } : {}),
    },
  ];
  return (
    <div className="min-w-0 space-y-5 pb-6">
      <nav
        className="text-ink-500 flex min-w-0 items-center gap-2 text-xs"
        aria-label="Fil d’Ariane"
      >
        <Link
          to={ROUTES.missions}
          className="hover:text-navy-900 focus-visible:outline-accent-300 shrink-0 rounded transition-colors focus-visible:outline-2"
        >
          Mes missions
        </Link>
        <ChevronRightIcon className="shrink-0" aria-hidden />
        <span aria-current="page" className="text-navy-800 truncate font-medium">
          {mission.title || mission.projectName}
        </span>
      </nav>
      <section
        aria-label="Fiche mission"
        className="border-navy-200/70 overflow-hidden rounded-[10px] border bg-white shadow-[inset_0_1px_0_0_white,0_4px_16px_-8px_rgb(15_42_95/0.12)]"
      >
        <div className="bg-navy-50/50 flex flex-wrap items-start justify-between gap-5 bg-linear-to-b from-white/95 to-transparent p-5 sm:p-6">
          <div className="flex min-w-0 flex-1 items-start gap-4">
            <span
              aria-hidden
              className="border-navy-200/60 text-navy-500 grid size-14 shrink-0 place-items-center rounded-[10px] border bg-white text-2xl shadow-[inset_0_1px_0_0_white]"
            >
              <BriefcaseIcon />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-ink-500 mb-1.5 text-xs">Fiche mission</p>
              <h1 className="text-navy-900 text-[26px] leading-tight font-semibold tracking-[-0.02em] wrap-anywhere sm:text-[28px]">
                {mission.title || mission.projectName}
              </h1>
              <p className="text-ink-600 mt-2 text-sm leading-6 wrap-anywhere">
                {[mission.clientName, mission.projectName].filter(Boolean).join(' · ')}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Badge tone={mission.isActive ? 'success' : 'muted'}>{mission.status.label}</Badge>
                {mission.reference && (
                  <span className="text-ink-500 text-xs">{mission.reference}</span>
                )}
              </div>
            </div>
          </div>
          <Button size="sm" onClick={() => setTab('timesheets')}>
            <TimesheetIcon />
            Déclarer mon CRA
          </Button>
        </div>
        <div className="border-navy-100 text-ink-500 flex flex-wrap items-center gap-x-7 gap-y-3 border-t px-5 py-4 text-xs sm:px-6">
          <span className="inline-flex items-center gap-2">
            <CalendarIcon className="shrink-0 text-base" />
            Du {formatMissionDate(mission.startDate)}
            {mission.endDate ? ` au ${formatMissionDate(mission.endDate)}` : ' · sans date de fin'}
          </span>
          <span className="inline-flex items-center gap-2">
            <TimesheetIcon className="shrink-0 text-base" />
            Un CRA par mission et par mois
          </span>
        </div>
      </section>
      <Tabs
        id={tabsId}
        label="Rubriques de la fiche mission"
        size="sm"
        tabs={tabs}
        value={tab}
        onChange={setTab}
        className="flex w-full bg-white/90 p-1.5"
      />
      <div className="min-h-72">
        <TabPanel
          id={`${tabsId}-general-panel`}
          labelledBy={`${tabsId}-general`}
          hidden={tab !== 'general'}
        >
          <div className="grid items-start gap-5 lg:grid-cols-2">
            <DetailSection title="Affectation" icon={<BuildingContactsIcon />}>
              <FieldGrid>
                <Field label="Intitulé de mission" value={mission.title || 'Non renseigné'} />
                <Field label="Référence" value={mission.reference || 'Non renseignée'} />
                <Field label="Projet" value={mission.projectName} />
                <Field label="Client" value={mission.clientName || 'Non renseigné'} />
                <Field
                  label="Statut"
                  value={
                    <Badge tone={mission.isActive ? 'success' : 'muted'}>
                      {mission.status.label}
                    </Badge>
                  }
                />
              </FieldGrid>
            </DetailSection>
            <div className="space-y-5">
              <DetailSection title="Période de prestation" icon={<CalendarIcon />}>
                <FieldGrid>
                  <Field label="Début de mission" value={formatMissionDate(mission.startDate)} />
                  <Field
                    label="Fin de mission"
                    value={
                      mission.endDate ? formatMissionDate(mission.endDate) : 'Sans date de fin'
                    }
                  />
                </FieldGrid>
                <p className="text-ink-500 border-navy-100 mt-5 border-t pt-4 text-xs leading-6">
                  Les déclarations d’activité sont limitées aux dates de cette mission.
                </p>
              </DetailSection>
              <DetailSection title="Droits annuels à congés" icon={<CalendarIcon />}>
                <FieldGrid>
                  <Field
                    label="Congés payés"
                    value={`${mission.leaveEntitlement.paidLeave} jours`}
                  />
                  <Field label="RTT" value={`${mission.leaveEntitlement.rtt} jours`} />
                  <Field label="Ancienneté" value={`${mission.leaveEntitlement.seniority} jours`} />
                  <Field label="Total annuel" value={`${mission.leaveEntitlement.total} jours`} />
                </FieldGrid>
              </DetailSection>
            </div>
          </div>
        </TabPanel>
        <TabPanel
          id={`${tabsId}-financial-panel`}
          labelledBy={`${tabsId}-financial`}
          hidden={tab !== 'financial'}
        >
          <DetailSection title="Conditions financières" icon={<InvoiceIcon />}>
            <div className="grid items-start gap-6 lg:grid-cols-2">
              <dl className="border-navy-200/70 bg-navy-50/40 rounded-[10px] border bg-linear-to-b from-white/90 to-transparent p-5 shadow-[inset_0_1px_0_0_white]">
                <dt className="text-ink-500 text-xs">CJM · Coût journalier moyen</dt>
                <dd className="text-navy-900 mt-2 text-[28px] font-semibold tracking-[-0.02em] tabular-nums">
                  {cost}
                  {mission.costDailyRate !== null && (
                    <span className="text-ink-500 ml-2 text-xs font-normal">/ jour</span>
                  )}
                </dd>
              </dl>
              <FieldGrid>
                <Field label="Devise de la mission" value={mission.currency} />
                <Field
                  label="Tarif applicable au"
                  value={
                    mission.costDailyRate !== null
                      ? formatMissionDate(mission.costAsOf)
                      : 'Non renseigné'
                  }
                />
              </FieldGrid>
            </div>
          </DetailSection>
        </TabPanel>
        <TabPanel
          id={`${tabsId}-timesheets-panel`}
          labelledBy={`${tabsId}-timesheets`}
          hidden={tab !== 'timesheets'}
        >
          <div className="space-y-5">
            {tab === 'timesheets' && (
              <TimesheetDeclaration
                key={mission.id}
                missions={[mission]}
                initialMissionId={mission.id}
              />
            )}
            <DetailSection
              title="CRA de cette mission"
              icon={<TimesheetIcon />}
              {...(sheets.data ? { count: sheets.data.meta.total } : {})}
            >
              {sheets.isPending ? (
                <LoadingState>Chargement des CRA…</LoadingState>
              ) : sheets.isError ? (
                <ErrorNotice
                  onRetry={() => {
                    void sheets.refetch();
                  }}
                >
                  {sheets.error.message}
                </ErrorNotice>
              ) : sheets.data.items.length === 0 ? (
                <p className="text-ink-500 py-5 text-center text-sm">
                  Aucun CRA sur cette mission. Choisissez un mois ci-dessus pour commencer.
                </p>
              ) : (
                <ul className="divide-navy-100 divide-y">
                  {sheets.data.items.map((sheet) => (
                    <li key={sheet.id}>
                      <Link
                        to={ROUTES.timesheet(sheet.id)}
                        className="hover:bg-navy-50/70 focus-visible:ring-accent-700/25 flex flex-wrap items-center gap-3 rounded-lg px-3 py-4 transition-colors focus-visible:ring-2 focus-visible:outline-none"
                      >
                        <span className="border-navy-200/60 text-navy-500 grid size-9 shrink-0 place-items-center rounded-lg border bg-white">
                          <TimesheetIcon />
                        </span>
                        <span className="min-w-36 flex-1">
                          <strong className="text-navy-900 block text-sm font-medium capitalize">
                            {formatMonth(sheet.periodMonth)}
                          </strong>
                          <span className="text-ink-500 text-xs">
                            {formatDays(sheet.totals.workedDays)} j travaillés
                          </span>
                        </span>
                        <Badge tone={STATE_TONES[sheet.state.code] ?? 'neutral'}>
                          {sheet.state.label}
                        </Badge>
                        <ChevronRightIcon className="text-ink-400 shrink-0" />
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </DetailSection>
          </div>
        </TabPanel>
      </div>
    </div>
  );
};

export const MissionDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const query = useMission(id);
  if (query.isPending) return <LoadingState>Chargement de votre mission…</LoadingState>;
  if (query.isError)
    return (
      <ErrorNotice
        onRetry={() => {
          void query.refetch();
        }}
      >
        {query.error.message}
      </ErrorNotice>
    );
  return <MissionDetails key={query.data.id} mission={query.data} />;
};
